import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  getSalesOverview,
  getSalesTeam,
  getSalesMeetings,
  getSalesReport,
  getSdrOverview,
  getSdrActivity,
  type ReportPeriod,
  type SalesPeriodFilter,
} from '../api/projectManagement';

export function useSalesOverview(filter: SalesPeriodFilter | null) {
  return useQuery({
    queryKey: ['project-management', 'sales', 'overview', filter],
    queryFn: () => getSalesOverview(filter),
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
    // Keep the previous payload while a new quarter loads so the pills don't flash away.
    placeholderData: keepPreviousData,
  });
}

export function useSalesTeam() {
  return useQuery({
    queryKey: ['project-management', 'sales', 'team'],
    queryFn: getSalesTeam,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });
}

export function useSalesMeetings(params: { from: string; to: string }) {
  return useQuery({
    queryKey: ['project-management', 'sales', 'meetings', params.from, params.to],
    queryFn: () => getSalesMeetings(params),
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
    // The API time-bounds this at 20 s; a retry would double the skeleton time before the error shows.
    retry: false,
  });
}

export function useSalesReport(params: { period: ReportPeriod; count: number }) {
  return useQuery({
    queryKey: ['project-management', 'sales', 'report', params.period, params.count],
    queryFn: () => getSalesReport(params),
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });
}

export function useSdrOverview(params?: { from: string; to: string }) {
  return useQuery({
    queryKey: ['project-management', 'sdr', 'overview', params?.from ?? null, params?.to ?? null],
    queryFn: () => getSdrOverview(params),
    placeholderData: keepPreviousData,
    // The API time-bounds this at 25 s; a retry would double the skeleton time before the error shows.
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });
}

export function useSdrActivity() {
  return useQuery({
    queryKey: ['project-management', 'sdr', 'activity'],
    queryFn: getSdrActivity,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });
}

